import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Card, CardContent } from './ui/card';
import { Dialog, DialogContent, DialogTitle } from './ui/dialog';
import api from '../lib/api';
import DetailSearchSort, { filterAndSortRows } from './DetailSearchSort';

const API_BASE = process.env.REACT_APP_BACKEND_URL;
const SIZE_ORDER = ['S','M','L','XL','2XL','3XL','4XL','5XL','6XL','7XL','8XL','9XL','Free Size','32','34','36','38','40','42'];
const fmtDate = (d) => { if (!d) return ''; const p = d.split('-'); return p.length === 3 && p[0].length === 4 ? `${p[2]}-${p[1]}-${p[0].slice(-2)}` : d; };
const STATUS_COLORS = { packed: 'bg-blue-100 text-blue-700', dispatched: 'bg-green-100 text-green-700', cancelled: 'bg-red-100 text-red-700' };
const statusLabel = (s) => (s || '').replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());

const SalesEntryDetail = ({ user }) => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [se, setSe] = useState(null);
  const [customers, setCustomers] = useState([]);
  const [brands, setBrands] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [lightboxImg, setLightboxImg] = useState(null);
  const [searchDesign, setSearchDesign] = useState('');
  const [sortBy, setSortBy] = useState('');

  const canManage = user?.role === 'admin' || user?.role === 'super_admin';

  const [agents, setAgents] = useState([]);
  const [distributors, setDistributors] = useState([]);

  const loadData = useCallback(async () => {
    try {
      const [seRes, custRes, brandRes, prodRes, agentRes, distRes] = await Promise.allSettled([
        api.get(`/sales-entries/${id}`), api.get('/customers'), api.get('/brands'), api.get('/products'),
        api.get('/agents'), api.get('/distributors'),
      ]);
      if (seRes.status === 'fulfilled') setSe(seRes.value.data); else throw new Error();
      if (custRes.status === 'fulfilled') setCustomers(custRes.value.data);
      if (brandRes.status === 'fulfilled') setBrands(brandRes.value.data);
      if (prodRes.status === 'fulfilled') setProducts(prodRes.value.data);
      if (agentRes.status === 'fulfilled') setAgents(agentRes.value.data);
      if (distRes.status === 'fulfilled') setDistributors(distRes.value.data);
    } catch { toast.error('Failed to load SE'); navigate('/sales-entry'); }
    finally { setLoading(false); }
  }, [id, navigate]);

  useEffect(() => { loadData(); }, [loadData]);

  const getCustomerName = (cid) => { const c = customers.find(x => x.id === cid); return c?.company_name || c?.name || 'Unknown'; };
  const getBrandName = (bid) => brands.find(b => b.id === bid)?.name || '';
  const getProductImages = (pid) => products.find(p => p.id === pid)?.images || [];

  const totalQty = se?.total_qty || 0;
  const totalAmount = se?.total_amount || 0;

  const deleteSE = async () => {
    if (!window.confirm('Delete this Sales Entry? Stock will be reversed.')) return;
    try { await api.delete(`/sales-entries/${se.id}`); toast.success('SE deleted & stock reversed'); navigate('/sales-entry'); }
    catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  if (loading) return <div className="flex items-center justify-center h-64"><div className="w-8 h-8 border-4 border-emerald-600 border-t-transparent rounded-full animate-spin"></div></div>;
  if (!se) return null;

  const allItems = se.items || [];
  const usedSizes = new Set();
  allItems.forEach(item => (item.variants || []).forEach(v => usedSizes.add(v.size)));
  const activeSizes = SIZE_ORDER.filter(s => usedSizes.has(s));

  let sr = 0, grandQty = 0, grandAmount = 0;
  const rows = [];
  allItems.forEach((item) => {
    const colorMap = {}, colorOrder = [];
    (item.variants || []).forEach(v => {
      const c = v.color || 'Other';
      if (!colorMap[c]) { colorMap[c] = { sizes: {}, qty: 0, wsps: {}, discounts: {} }; colorOrder.push(c); }
      colorMap[c].sizes[v.size] = (colorMap[c].sizes[v.size] || 0) + (v.qty || 0);
      colorMap[c].qty += (v.qty || 0);
      colorMap[c].wsps[v.size] = v.wsp || 0;
      colorMap[c].discounts[v.size] = v.discount_percent || 0;
    });
    colorOrder.sort((a, b) => (a || '').localeCompare(b || '')).forEach(color => {
      sr++;
      const d = colorMap[color];
      let amount = 0;
      activeSizes.forEach(s => { amount += (d.sizes[s] || 0) * (d.wsps[s] || 0); });
      grandQty += d.qty; grandAmount += amount;
      const wspGroups = [];
      let currentGroup = null;
      activeSizes.forEach(s => {
        if ((d.sizes[s] || 0) === 0) return;
        const wsp = d.wsps[s] || 0;
        if (currentGroup && currentGroup.wsp === wsp) { currentGroup.sizes.push(s); }
        else { currentGroup = { wsp, sizes: [s] }; wspGroups.push(currentGroup); }
      });
      rows.push({ sr, item, color, sizes: d.sizes, wsps: d.wsps, discounts: d.discounts, qty: d.qty, amount, wspGroups, boxNumber: item.box_number });
    });
  });

  return (
    <div className="p-6 max-w-6xl mx-auto space-y-6 animate-fadeIn" data-testid="se-detail-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="sm" onClick={() => navigate(-1)} data-testid="se-detail-back" className="text-gray-500 hover:text-gray-900">
            <svg className="w-5 h-5 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7" /></svg>Back
          </Button>
          <div>
            <div className="flex items-center gap-3 flex-wrap">
              <h1 className="text-2xl font-bold text-gray-900 font-mono" data-testid="se-detail-number">{se.entry_number}</h1>
              <Badge className={`${STATUS_COLORS[se.status] || 'bg-gray-100'} text-sm`} data-testid="se-detail-status">{statusLabel(se.status)}</Badge>
              {se.tally_status && <Badge className={`${se.tally_status === 'done' ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'} text-xs`}>Tally: {se.tally_status}</Badge>}
            </div>
            <h2 className="text-xl font-bold text-blue-800 mt-1" data-testid="se-detail-customer">{getCustomerName(se.customer_id)}</h2>
            <div className="flex items-center gap-3 flex-wrap mt-1 text-sm text-gray-500">
              <span>{fmtDate(se.entry_date)}</span>
              {(() => { const c = customers.find(x => x.id === se.customer_id); return c ? (
                <>
                  {c.city && <span>{c.city}{c.state ? `, ${c.state}` : ''}</span>}
                  {c.agent_id && <span className="text-indigo-600">Agent: {agents.find(a => a.id === c.agent_id)?.name || ''}</span>}
                  {c.distributor_id && <span className="text-teal-600">Dist: {distributors.find(d => d.id === c.distributor_id)?.company_name || ''}</span>}
                </>
              ) : null; })()}
              {se.channel && <span className="capitalize">{se.channel.replace(/_/g, ' ')}</span>}
              {se.bill_type && <span className="capitalize">{se.bill_type}</span>}
              {se.transport_name && <span>Transport: {se.transport_name}</span>}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {canManage && (
            <>
              <Button size="sm" variant="outline" className="text-blue-600 border-blue-300" onClick={() => navigate('/create-sales-entry', { state: { editSE: se } })} data-testid="se-detail-edit">
                <svg className="w-4 h-4 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" /></svg>Edit
              </Button>
              <Button size="sm" variant="outline" className="text-red-600 border-red-300" onClick={deleteSE} data-testid="se-detail-delete">
                <svg className="w-4 h-4 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>Delete
              </Button>
            </>
          )}
        </div>
      </div>

      {/* Summary */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4" data-testid="se-detail-summary">
        <Card><CardContent className="p-4 text-center"><p className="text-xs text-gray-500 mb-1">Total Amount</p><p className="text-xl font-bold text-gray-900">₹{totalAmount.toLocaleString('en-IN')}</p></CardContent></Card>
        <Card><CardContent className="p-4 text-center"><p className="text-xs text-gray-500 mb-1">Total Qty</p><p className="text-xl font-bold text-blue-700">{totalQty} pcs</p></CardContent></Card>
        <Card><CardContent className="p-4 text-center"><p className="text-xs text-gray-500 mb-1">Items</p><p className="text-xl font-bold text-emerald-700">{allItems.length}</p></CardContent></Card>
        <Card><CardContent className="p-4 text-center"><p className="text-xs text-gray-500 mb-1">Channel</p><p className="text-lg font-bold text-gray-700 capitalize">{se.channel || se.order_source || '-'}</p></CardContent></Card>
        <Card><CardContent className="p-4 text-center"><p className="text-xs text-gray-500 mb-1">Linked SO</p><p className="text-lg font-bold text-indigo-600 cursor-pointer hover:underline" onClick={() => { if (se.linked_so_id) navigate(`/sales-order/${se.linked_so_id}`); }}>{se.linked_so_id ? 'View SO' : 'None'}</p></CardContent></Card>
      </div>

      {/* Info */}
      <div className="flex gap-6 flex-wrap text-sm text-gray-600 bg-gray-50 p-3 rounded-lg">
        {se.invoice_number && <span>Invoice: <strong>{se.invoice_number}</strong></span>}
        {se.transport_name && <span>Transport: <strong>{se.transport_name}</strong></span>}
        {se.created_at && <span>Created: <strong>{new Date(se.created_at).toLocaleString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}</strong></span>}
        {se.notes && <span className="italic text-gray-500">Notes: {se.notes}</span>}
        {se.freight_charges > 0 && <span>Freight: <strong>₹{se.freight_charges.toLocaleString('en-IN')}</strong></span>}
      </div>

      {/* Pivot Table */}
      <Card data-testid="se-detail-items">
        <CardContent className="p-0">
          <DetailSearchSort search={searchDesign} setSearch={setSearchDesign} sortBy={sortBy} setSortBy={setSortBy} sortOptions={['rate', 'amount']} testIdPrefix="se-detail" />
          <div className="overflow-x-auto">
            <table className="w-full text-sm border-collapse" data-testid="se-excel-table">
              <thead>
                <tr className="bg-[#1E1E23] text-white text-xs uppercase tracking-wider">
                  <th className="px-3 py-2.5 text-center font-medium border border-gray-700 w-10">SR</th>
                  <th className="px-2 py-2.5 text-center font-medium border border-gray-700 w-16">Image</th>
                  <th className="px-3 py-2.5 text-left font-medium border border-gray-700">Design</th>
                  <th className="px-3 py-2.5 text-left font-medium border border-gray-700">Brand</th>
                  <th className="px-3 py-2.5 text-left font-medium border border-gray-700">Colour</th>
                  {activeSizes.map(s => <th key={s} className="px-2 py-2.5 text-center font-medium border border-gray-700 min-w-[42px]">{s}</th>)}
                  <th className="px-2 py-2.5 text-right font-medium border border-gray-700 w-14">Disc%</th>
                  <th className="px-3 py-2.5 text-center font-medium border border-gray-700">Qty</th>
                  <th className="px-3 py-2.5 text-right font-medium border border-gray-700">Amount</th>
                </tr>
              </thead>
              <tbody>
                {filterAndSortRows(rows, searchDesign, sortBy).map((r, ri) => {
                  const images = getProductImages(r.item.product_id);
                  const firstImg = images[0];
                  return (
                    <React.Fragment key={ri}>
                      <tr className="bg-white hover:bg-emerald-50/40 transition-colors">
                        <td className="px-3 py-2 text-center text-gray-500 border border-gray-500 font-mono text-xs" rowSpan="2">{r.sr}</td>
                        <td className="px-2 py-1.5 text-center border border-gray-500" rowSpan="2">
                          {firstImg ? <img src={`${API_BASE}${firstImg}`} alt={r.item.design_code} className="w-11 h-11 rounded object-cover cursor-pointer hover:ring-2 hover:ring-emerald-400 transition-all mx-auto" onClick={() => setLightboxImg(`${API_BASE}${firstImg}`)} /> : <div className="w-11 h-11 bg-gray-100 rounded flex items-center justify-center text-gray-400 mx-auto"><svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" /></svg></div>}
                        </td>
                        <td className="px-3 py-2 font-semibold text-gray-900 border border-gray-500" rowSpan="2">{r.item.design_code}</td>
                        <td className="px-3 py-2 text-gray-600 border border-gray-500 text-xs" rowSpan="2">{getBrandName(r.item.brand_id)}</td>
                        <td className="px-3 py-2 font-medium text-gray-800 border border-gray-500" rowSpan="2">{r.color}</td>
                        {activeSizes.map(s => {
                          const q = r.sizes[s] || 0;
                          return <td key={s} className="px-2 py-2 text-center border border-gray-500">{q > 0 ? <span className="font-medium">{q}</span> : <span className="text-gray-300">-</span>}</td>;
                        })}
                        {(() => { const disc = Object.values(r.discounts).find(d => d > 0); return <td className="px-2 py-2 text-right text-gray-700 border border-gray-500" rowSpan="2">{disc ? `${disc}%` : <span className="text-gray-300">—</span>}</td>; })()}
                        <td className="px-3 py-2 text-center font-bold text-gray-900 border border-gray-500" rowSpan="2">{r.qty}</td>
                        <td className="px-3 py-2 text-right font-semibold text-gray-900 border border-gray-500" rowSpan="2">{r.amount > 0 ? `₹${Math.round(r.amount).toLocaleString('en-IN')}` : ''}</td>
                      </tr>
                      <tr className="bg-white">
                        {(() => {
                          const cells = []; let sIdx = 0;
                          while (sIdx < activeSizes.length) {
                            const s = activeSizes[sIdx]; const q = r.sizes[s] || 0;
                            if (q === 0) { cells.push(<td key={sIdx} className="border border-gray-500"></td>); sIdx++; continue; }
                            const group = r.wspGroups.find(g => g.sizes.includes(s));
                            if (group) { let span = 0; for (let i = sIdx; i < activeSizes.length; i++) { if (group.sizes.includes(activeSizes[i])) span++; else break; } cells.push(<td key={sIdx} colSpan={span} className="px-2 py-1.5 text-center border border-gray-500"><span className="font-semibold text-blue-800 text-xs">@₹{group.wsp.toLocaleString('en-IN')}</span></td>); sIdx += span; }
                            else { cells.push(<td key={sIdx} className="border border-gray-500"></td>); sIdx++; }
                          }
                          return cells;
                        })()}
                      </tr>
                    </React.Fragment>
                  );
                })}
              </tbody>
              <tfoot>
                <tr className="bg-[#218C59] text-white font-bold text-sm">
                  <td className="px-3 py-2.5 border border-green-700" colSpan="5">TOTAL</td>
                  {activeSizes.map(s => { const t = rows.reduce((sum, r) => sum + (r.sizes[s] || 0), 0); return <td key={s} className="px-2 py-2.5 text-center border border-green-700">{t || ''}</td>; })}
                  <td className="px-3 py-2.5 border border-green-700"></td>
                  <td className="px-3 py-2.5 text-center border border-green-700">{grandQty}</td>
                  <td className="px-3 py-2.5 text-right border border-green-700">₹{Math.round(grandAmount).toLocaleString('en-IN')}</td>
                </tr>
              </tfoot>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Lightbox */}
      <Dialog open={!!lightboxImg} onOpenChange={() => setLightboxImg(null)}>
        <DialogContent className="max-w-3xl p-2 bg-black/90 border-none" aria-describedby={undefined}>
          <DialogTitle className="sr-only">Product Image</DialogTitle>
          <img src={lightboxImg} alt="Full size" className="w-full h-auto max-h-[85vh] object-contain rounded" />
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SalesEntryDetail;
